import React, { useState } from 'react'
import { supabase } from '../supabaseClient'

export default function Admin() {
  const [authed, setAuthed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('players')
  const [activeSection, setActiveSection] = useState('worldcup')

  React.useEffect(() => {
    async function checkAdmin() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setLoading(false); return }
      const { data } = await supabase.rpc('is_admin')
      if (data) setAuthed(true)
      setLoading(false)
    }
    checkAdmin()
  }, [])

  async function handleLogout() {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  if (loading) return (
    <div>
      <nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav>
      <div className="container"><p className="text-muted">Loading...</p></div>
    </div>
  )

  if (!authed) return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a href="/login?redirect=/admin" className="btn btn-primary" style={{ padding: '7px 16px', fontSize: '13px' }}>Log in</a>
          <a href="/" className="btn btn-secondary" style={{ padding: '7px 16px', fontSize: '13px' }}>Home</a>
        </div>
      </nav>
      <div style={{ textAlign: 'center', padding: '80px 24px 0' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
        <h1 style={{ fontFamily: 'var(--ff-d)', fontSize: '36px', marginBottom: '8px' }}>Admin Panel</h1>
        <p style={{ color: 'var(--text-2)' }}>You need to be logged in as an admin to access this page.</p>
      </div>
    </div>
  )

  const TABS = ['players', 'results', 'bonuses', 'leaderboard', 'bracket', 'phase', 'stats']
  const SECTIONS = ['worldcup', 'lms', 'platform']
  const sectionLabels = { worldcup: 'World Cup', lms: 'LMS', platform: 'Platform' }

  return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', gap: '4px' }}>
          {SECTIONS.map(s => (
            <button key={s} onClick={() => setActiveSection(s)} style={{
              padding: '6px 16px', borderRadius: 'var(--r)', fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', border: 'none',
              background: activeSection === s ? 'var(--gold)' : 'transparent',
              color: activeSection === s ? '#fff' : 'var(--text-2)'
            }}>
              {sectionLabels[s]}
            </button>
          ))}
        </div>
        <button className="btn btn-secondary" onClick={handleLogout} style={{ padding: '7px 14px', fontSize: '13px' }}>Log out</button>
      </nav>

      <div className="container">
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '28px' }}>{sectionLabels[activeSection]}</h1>
        </div>

        {activeSection === 'worldcup' && (
          <>
            <div className="tab-bar">
              {TABS.map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`tab-btn ${activeTab === tab ? 'active' : ''}`}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            {activeTab === 'players' && <PlayersTab />}
            {activeTab === 'results' && <ResultsTab />}
            {activeTab === 'bonuses' && <BonusesTab />}
            {activeTab === 'leaderboard' && <LeaderboardTab />}
            {activeTab === 'bracket' && <BracketTab />}
            {activeTab === 'phase' && <PhaseTab />}
            {activeTab === 'stats' && <StatsTab />}
          </>
        )}

        {activeSection === 'lms' && <LMSAdminTab />}

        {activeSection === 'platform' && (
          <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ fontFamily: 'var(--ff-d)', fontSize: '24px', marginBottom: '8px' }}>Platform</p>
            <p style={{ color: 'var(--text-2)', fontSize: '14px' }}>Coming soon - User management and platform settings</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── STATS TAB ──────────────────────────────────────────────────────────────

function StatsTab() {
  const [matches, setMatches] = useState([])
  const [stats, setStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [activePhase, setActivePhase] = useState('group')
  const [activeGroup, setActiveGroup] = useState('A')
  const [activeKo, setActiveKo] = useState('knockout_r32')
  const GROUPS = ['A','B','C','D','E','F','G','H','I','J','K','L']
  const KO_PHASES = [
    { key: 'knockout_r32', label: 'Round of 32' },
    { key: 'knockout_r16', label: 'Round of 16' },
    { key: 'knockout_qf', label: 'Quarter-finals' },
    { key: 'knockout_sf', label: 'Semi-finals' },
    { key: 'knockout_3rd', label: '3rd Place' },
    { key: 'knockout_final', label: 'Final' }
  ]

  React.useEffect(() => { loadStats() }, [])

  async function loadStats() {
    const { data: matchData } = await supabase
      .from('matches')
      .select('*, home:home_team_id(name, flag_emoji), away:away_team_id(name, flag_emoji)')
      .order('match_number')

    const { data: predData } = await supabase
      .rpc('get_match_prediction_stats')

    const statsMap = {}
    ;(predData || []).forEach(p => {
      const key = p.match_id
      if (!statsMap[key]) statsMap[key] = { total: 0, scores: {} }
      const count = parseInt(p.prediction_count)
      statsMap[key].total += count
      const score = `${p.predicted_home_goals}-${p.predicted_away_goals}`
      statsMap[key].scores[score] = count
    })

    Object.keys(statsMap).forEach(matchId => {
      const s = statsMap[matchId]
      const sorted = Object.entries(s.scores).sort((a, b) => b[1] - a[1])
      s.topScore = sorted[0]?.[0] || null
      s.topCount = sorted[0]?.[1] || 0
      s.allScores = sorted
    })

    setMatches(matchData || [])
    setStats(statsMap)
    setLoading(false)
  }

  function getOutcome(score, match) {
    if (!match.result_confirmed || score === null) return null
    const [ph, pa] = score.split('-').map(Number)
    const ah = match.actual_home_goals
    const aw = match.actual_away_goals
    if (ph === ah && pa === aw) return 'exact'
    const predResult = ph > pa ? 'home' : ph < pa ? 'away' : 'draw'
    const actualResult = ah > aw ? 'home' : ah < aw ? 'away' : 'draw'
    if (predResult === actualResult) return 'result'
    return 'wrong'
  }

  function MatchStatRow({ match }) {
    const s = stats[match.id]
    const total = s?.total || 0
    const topScore = s?.topScore || null
    const topCount = s?.topCount || 0
    const pct = total > 0 ? Math.round((topCount / total) * 100) : 0
    const outcome = getOutcome(topScore, match)

    return (
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-sub)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '160px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500' }}>
              {match.home?.flag_emoji} {match.home?.name} vs {match.away?.name} {match.away?.flag_emoji}
            </span>
            {match.result_confirmed && (
              <span style={{ marginLeft: '10px', fontSize: '12px', color: 'var(--text-3)' }}>
                Actual: {match.actual_home_goals}-{match.actual_away_goals}
              </span>
            )}
          </div>

          {total === 0 ? (
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>No predictions yet</span>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'var(--ff-d)', fontSize: '22px', fontWeight: '700',
                  color: outcome === 'exact' ? 'var(--green)' : outcome === 'result' ? 'var(--gold)' : outcome === 'wrong' ? 'var(--red)' : 'var(--text)'
                }}>
                  {topScore}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>crowd pick</div>
              </div>
              <div style={{ minWidth: '120px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>
                  <span>{topCount} of {total}</span>
                  <span>{pct}%</span>
                </div>
                <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: outcome === 'exact' ? 'var(--green)' : outcome === 'result' ? 'var(--gold)' : 'var(--gold)', borderRadius: '3px' }} />
                </div>
              </div>
              {outcome === 'exact' && <span style={{ fontSize: '11px', color: 'var(--green)', fontWeight: '600' }}>✓ Exact</span>}
              {outcome === 'result' && <span style={{ fontSize: '11px', color: 'var(--gold)', fontWeight: '600' }}>~ Result</span>}
              {outcome === 'wrong' && <span style={{ fontSize: '11px', color: 'var(--red)', fontWeight: '600' }}>✗ Wrong</span>}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (loading) return <p className="text-muted">Loading stats...</p>

  const groupMatches = matches.filter(m => m.phase === 'group')
  const confirmedMatches = groupMatches.filter(m => m.result_confirmed)
  const crowdExact = confirmedMatches.filter(m => getOutcome(stats[m.id]?.topScore, m) === 'exact').length
  const crowdResult = confirmedMatches.filter(m => getOutcome(stats[m.id]?.topScore, m) === 'result').length
  const crowdWrong = confirmedMatches.filter(m => getOutcome(stats[m.id]?.topScore, m) === 'wrong').length

  const currentGroupMatches = matches.filter(m => m.phase === 'group' && m.group_code === activeGroup)
  const currentKoMatches = matches.filter(m => m.phase === activeKo)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0, marginBottom: '4px' }}>Crowd Wisdom</h2>
          <p style={{ color: 'var(--text-2)', fontSize: '13px' }}>Most predicted scoreline per match across all entries</p>
        </div>
        <button className="btn btn-secondary" onClick={loadStats} style={{ fontSize: '13px', padding: '7px 14px' }}>Refresh</button>
      </div>

      {confirmedMatches.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '24px' }}>
          {[
            [crowdExact, 'Exact score', 'var(--green)'],
            [crowdResult, 'Correct result', 'var(--gold)'],
            [crowdWrong, 'Wrong', 'var(--red)'],
            [confirmedMatches.length, 'Matches played', 'var(--text-2)']
          ].map(([v, l, c]) => (
            <div key={l} className="stat-card">
              <div className="stat-num" style={{ color: c }}>{v}</div>
              <div className="stat-label">{l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="tab-bar" style={{ marginBottom: '16px' }}>
        <button className={`tab-btn ${activePhase === 'group' ? 'active' : ''}`} onClick={() => setActivePhase('group')}>Group Stage</button>
        <button className={`tab-btn ${activePhase === 'knockout' ? 'active' : ''}`} onClick={() => setActivePhase('knockout')}>Knockout</button>
      </div>

      {activePhase === 'group' && (
        <>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {GROUPS.map(g => (
              <button key={g} onClick={() => setActiveGroup(g)} style={{
                padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: '13px', cursor: 'pointer',
                fontFamily: 'var(--ff-d)', fontWeight: '600', letterSpacing: '.04em',
                background: activeGroup === g ? 'var(--gold-bg)' : 'transparent',
                color: activeGroup === g ? 'var(--gold)' : 'var(--text-2)',
                border: activeGroup === g ? '1px solid var(--gold-border)' : '1px solid var(--border)'
              }}>Group {g}</button>
            ))}
          </div>
          <div className="card-flat">
            {currentGroupMatches.map(match => <MatchStatRow key={match.id} match={match} />)}
          </div>
        </>
      )}

      {activePhase === 'knockout' && (
        <>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {KO_PHASES.map(p => (
              <button key={p.key} onClick={() => setActiveKo(p.key)} style={{
                padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: '13px', cursor: 'pointer',
                fontFamily: 'var(--ff-d)', fontWeight: '600', letterSpacing: '.04em',
                background: activeKo === p.key ? 'var(--gold-bg)' : 'transparent',
                color: activeKo === p.key ? 'var(--gold)' : 'var(--text-2)',
                border: activeKo === p.key ? '1px solid var(--gold-border)' : '1px solid var(--border)'
              }}>{p.label}</button>
            ))}
          </div>
          <div className="card-flat">
            {currentKoMatches.length === 0
              ? <p className="text-muted" style={{ padding: '24px', textAlign: 'center' }}>No predictions submitted for this round yet.</p>
              : currentKoMatches.map(match => <MatchStatRow key={match.id} match={match} />)
            }
          </div>
        </>
      )}
    </div>
  )
}

// ─── PLAYERS TAB ────────────────────────────────────────────────────────────

function PlayersTab() {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [noEntryUsers, setNoEntryUsers] = useState([])
  const [search, setSearch] = useState('')

  React.useEffect(() => { loadEntries() }, [])

  async function loadEntries() {
    const { data } = await supabase
      .from('entries')
      .select('*, users(name, email, phone)')
      .order('created_at')

    const { data: predData } = await supabase
      .rpc('get_prediction_counts')

    const predCounts = {}
    ;(predData || []).forEach(p => {
      predCounts[p.entry_id] = parseInt(p.prediction_count)
    })

    const entriesWithCounts = (data || []).map(e => ({
      ...e,
      predictions_count: predCounts[e.id] || 0
    }))

    setEntries(entriesWithCounts)

    const { data: allUsers } = await supabase
      .from('users')
      .select('id, name, email, phone, created_at')
      .order('created_at')
    const entryUserIds = new Set((data || []).map(e => e.user_id))
    setNoEntryUsers((allUsers || []).filter(u => !entryUserIds.has(u.id)))

    setLoading(false)
  }

  async function togglePaid(entryId, current) {
    setSaving(entryId)
    await supabase.from('entries').update({ paid: !current }).eq('id', entryId)
    await loadEntries()
    setSaving(null)
  }

  async function handleBlock(entryId, entryName) {
    if (!window.confirm(`Block "${entryName}"? This will hide it from the leaderboard.`)) return
    await supabase.from('entries').update({ paid: false }).eq('id', entryId)
    await loadEntries()
  }

  async function handleAdminDelete(entryId, entryName) {
    if (!window.confirm(`Permanently delete "${entryName}"? This cannot be undone.`)) return
    const { error: e1 } = await supabase.from('match_predictions').delete().eq('entry_id', entryId)
    if (e1) { alert(`Failed deleting match predictions: ${e1.message}`); return }
    const { error: e2 } = await supabase.from('bonus_predictions').delete().eq('entry_id', entryId)
    if (e2) { alert(`Failed deleting bonus predictions: ${e2.message}`); return }
    const { error: e3 } = await supabase.from('group_predictions').delete().eq('entry_id', entryId)
    if (e3) { alert(`Failed deleting group predictions: ${e3.message}`); return }
    const { error: e4 } = await supabase.from('entries').delete().eq('id', entryId)
    if (e4) { alert(`Failed deleting entry: ${e4.message}`); return }
    await loadEntries()
  }

  if (loading) return <p className="text-muted">Loading players...</p>

  const paid = entries.filter(e => e.paid).length
  const pending = entries.filter(e => !e.paid).length

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>All Entries ({entries.length})</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search player..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '7px 12px', fontSize: '13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', width: '200px' }}
          />
          <div style={{ display: 'flex', gap: '16px', fontSize: '13px' }}>
            <span style={{ color: 'var(--green)', fontWeight: '600' }}>{paid} paid</span>
            <span style={{ color: 'var(--text-3)' }}>{pending} pending</span>
          </div>
        </div>
      </div>

      <div className="card-flat">
        <table>
          <thead>
            <tr>
              <th>Player</th>
              <th>Email</th>
              <th>WhatsApp</th>
              <th>Entry</th>
              <th>Registered</th>
              <th style={{ textAlign: 'center' }}>Predictions</th>
              <th style={{ textAlign: 'center' }}>Paid</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.filter(e => !search || e.users?.name?.toLowerCase().includes(search.toLowerCase())).map(entry => (
              <tr key={entry.id}>
                <td style={{ fontWeight: '500' }}>{entry.users?.name}</td>
                <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{entry.users?.email}</td>
                <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{entry.users?.phone || '-'}</td>
                <td>
                  <button onClick={() => setSelectedEntry(entry)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gold)', fontSize: '13px', fontWeight: '600', padding: 0 }}>
                    {entry.entry_name}
                  </button>
                </td>
                <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{new Date(entry.created_at).toLocaleDateString()}</td>
                <td style={{ textAlign: 'center' }}>
                  {entry.predictions_count >= 72
                    ? <span style={{ color: 'var(--green)', fontSize: '13px', fontWeight: '600' }}>✓ Complete</span>
                    : entry.predictions_count > 0
                    ? <span style={{ color: 'var(--text-2)', fontSize: '13px' }}>{entry.predictions_count}/72</span>
                    : <span style={{ color: 'var(--red)', fontSize: '13px' }}>None</span>
                  }
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => togglePaid(entry.id, entry.paid)}
                    disabled={saving === entry.id}
                    style={{
                      width: '32px', height: '32px', borderRadius: 'var(--r-sm)',
                      border: entry.paid ? 'none' : '1px solid var(--border)',
                      background: entry.paid ? 'var(--gold)' : 'var(--surface)',
                      cursor: 'pointer', fontSize: '14px', fontWeight: '700',
                      color: entry.paid ? '#fff' : 'var(--text-3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto'
                    }}
                  >
                    {entry.paid ? '✓' : ''}
                  </button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                    {entry.paid && (
                      <button onClick={() => handleBlock(entry.id, entry.entry_name)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer', fontWeight: '600' }}>
                        Block
                      </button>
                    )}
                    <button onClick={() => handleAdminDelete(entry.id, entry.entry_name)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: 'var(--r-sm)', border: '1px solid rgba(220,38,38,.2)', background: 'rgba(220,38,38,.06)', color: 'var(--red)', cursor: 'pointer', fontWeight: '600' }}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length === 0 && <p className="text-muted" style={{ padding: '24px', textAlign: 'center' }}>No entries yet.</p>}
      </div>

      {selectedEntry && <EntryDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />}

      {noEntryUsers.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Registered, No Entry ({noEntryUsers.length})</h2>
            <span style={{ fontSize: '13px', color: 'var(--text-3)' }}>Signed up but haven't submitted an entry yet</span>
          </div>
          <div className="card-flat">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>WhatsApp</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {noEntryUsers.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: '500' }}>{u.name}</td>
                    <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{u.email}</td>
                    <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{u.phone || '-'}</td>
                    <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── RESULTS TAB ────────────────────────────────────────────────────────────

function ResultsTab() {
  const [matches, setMatches] = useState({})
  const [results, setResults] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [activeTab, setActiveTab] = useState('group')
  const GROUPS = ['A','B','C','D','E','F','G','H','I','J','K','L']
  const KO_PHASES = [
    { key: 'knockout_r32', label: 'Round of 32' },
    { key: 'knockout_r16', label: 'Round of 16' },
    { key: 'knockout_qf', label: 'Quarter-finals' },
    { key: 'knockout_sf', label: 'Semi-finals' },
    { key: 'knockout_3rd', label: '3rd Place' },
    { key: 'knockout_final', label: 'Final' }
  ]

  React.useEffect(() => { loadMatches() }, [])

  async function loadMatches() {
    const { data } = await supabase
      .from('matches')
      .select('*, home:home_team_id(id,name,flag_emoji), away:away_team_id(id,name,flag_emoji)')
      .order('match_number')

    const grouped = {}
    data?.forEach(m => {
      const key = m.phase === 'group' ? m.group_code : m.phase
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(m)
    })
    setMatches(grouped)

    const resultMap = {}
    data?.forEach(m => {
      if (m.actual_home_goals !== null && m.actual_away_goals !== null) {
        resultMap[m.id] = {
          home: m.actual_home_goals,
          away: m.actual_away_goals,
          went_to_penalties: m.went_to_penalties || false,
          penalty_winner_id: m.penalty_winner_id || ''
        }
      }
    })
    setResults(resultMap)
    setLoading(false)
  }

  async function handleSaveResults() {
    setSaving(true)
    setSaved(false)

    for (const [matchId, score] of Object.entries(results)) {
      if (score.home !== '' && score.away !== '' && score.home !== undefined && score.away !== undefined) {
        await supabase.from('matches').update({
          actual_home_goals: score.home,
          actual_away_goals: score.away,
          result_confirmed: true,
          went_to_penalties: score.went_to_penalties || false,
          penalty_winner_id: score.penalty_winner_id || null
        }).eq('id', matchId)
      }
    }

    await supabase.rpc('cascade_ko_teams')
    await supabase.rpc('calculate_scores')
    setSaved(true)
    setSaving(false)
  }

  function setScore(matchId, side, value) {
    setResults(prev => ({ ...prev, [matchId]: { ...prev[matchId], [side]: value === '' ? '' : parseInt(value) || 0 } }))
  }

  function togglePenalties(matchId, checked) {
    setResults(prev => ({ ...prev, [matchId]: { ...prev[matchId], went_to_penalties: checked, penalty_winner_id: '' } }))
  }

  function setPenaltyWinner(matchId, teamId) {
    setResults(prev => ({ ...prev, [matchId]: { ...prev[matchId], penalty_winner_id: teamId } }))
  }

  if (loading) return <p className="text-muted">Loading matches...</p>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Enter Results</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {saved && <span style={{ fontSize: '13px', color: 'var(--gold)', fontWeight: '500' }}>Saved</span>}
          <button className="btn btn-primary" onClick={handleSaveResults} disabled={saving} style={{ padding: '8px 18px', fontSize: '13px' }}>
            {saving ? 'Saving...' : 'Save Results'}
          </button>
        </div>
      </div>

      <div className="tab-bar" style={{ marginBottom: '16px' }}>
        <button className={`tab-btn ${!activeTab.startsWith('knockout_') && activeTab !== 'knockout' ? 'active' : ''}`} onClick={() => setActiveTab('group')}>Group Stage</button>
        <button className={`tab-btn ${activeTab === 'knockout' || activeTab.startsWith('knockout_') ? 'active' : ''}`} onClick={() => setActiveTab('knockout')}>Knockout</button>
      </div>

      {!activeTab.startsWith('knockout_') && activeTab !== 'knockout' && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {GROUPS.map(g => (
            <button key={g} onClick={() => setActiveTab(g)} style={{
              padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: '13px', cursor: 'pointer',
              fontFamily: 'var(--ff-d)', fontWeight: '600', letterSpacing: '.04em',
              background: activeTab === g ? 'var(--gold-bg)' : 'transparent',
              color: activeTab === g ? 'var(--gold)' : 'var(--text-2)',
              border: activeTab === g ? '1px solid var(--gold-border)' : '1px solid var(--border)'
            }}>Group {g}</button>
          ))}
        </div>
      )}

      {GROUPS.includes(activeTab) && (
        <div className="card-flat">
          {(matches[activeTab] || []).map(match => {
            const result = results[match.id] || {}
            return (
              <div key={match.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-sub)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: '500' }}>{match.home?.flag_emoji} {match.home?.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="number" min="0" max="20" className="score-input" value={result.home ?? ''} onChange={e => setScore(match.id, 'home', e.target.value)} />
                    <span style={{ color: 'var(--text-3)', fontSize: '14px', fontWeight: '700' }}>:</span>
                    <input type="number" min="0" max="20" className="score-input" value={result.away ?? ''} onChange={e => setScore(match.id, 'away', e.target.value)} />
                  </div>
                  <span style={{ flex: 1, textAlign: 'right', fontSize: '13px', fontWeight: '500' }}>{match.away?.name} {match.away?.flag_emoji}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {(activeTab === 'knockout' || activeTab.startsWith('knockout_')) && (
        <>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {KO_PHASES.map(p => (
              <button key={p.key} onClick={() => setActiveTab(p.key)} style={{
                padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: '13px', cursor: 'pointer',
                fontFamily: 'var(--ff-d)', fontWeight: '600', letterSpacing: '.04em',
                background: activeTab === p.key ? 'var(--gold-bg)' : 'transparent',
                color: activeTab === p.key ? 'var(--gold)' : 'var(--text-2)',
                border: activeTab === p.key ? '1px solid var(--gold-border)' : '1px solid var(--border)'
              }}>{p.label}</button>
            ))}
          </div>

          {activeTab.startsWith('knockout_') && (
            <div className="card-flat">
              {(matches[activeTab] || []).map(match => {
                const result = results[match.id] || {}
                const homeGoals = parseInt(result.home)
                const awayGoals = parseInt(result.away)
                const isDraw = !isNaN(homeGoals) && !isNaN(awayGoals) && homeGoals === awayGoals
                return (
                  <div key={match.id} style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-sub)' }}>
                    <p style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '10px', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                      M{match.match_number} - {match.match_label}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                      <span style={{ flex: 1, fontSize: '13px', fontWeight: '500' }}>{match.home?.flag_emoji} {match.home?.name || 'TBD'}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input type="number" min="0" max="20" className="score-input" value={result.home ?? ''} onChange={e => setScore(match.id, 'home', e.target.value)} />
                        <span style={{ color: 'var(--text-3)', fontSize: '14px', fontWeight: '700' }}>:</span>
                        <input type="number" min="0" max="20" className="score-input" value={result.away ?? ''} onChange={e => setScore(match.id, 'away', e.target.value)} />
                      </div>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: '13px', fontWeight: '500' }}>{match.away?.name || 'TBD'} {match.away?.flag_emoji}</span>
                    </div>
                    {isDraw && (
                      <div style={{ marginTop: '10px', padding: '10px 12px', background: 'var(--gold-bg)', borderRadius: 'var(--r)', border: '1px solid var(--gold-border)' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-2)', marginBottom: '8px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={result.went_to_penalties || false} onChange={e => togglePenalties(match.id, e.target.checked)} style={{ width: 'auto' }} />
                          Match went to penalties
                        </label>
                        {result.went_to_penalties && match.home && match.away && (
                          <div>
                            <p style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '8px' }}>Penalty winner:</p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => setPenaltyWinner(match.id, match.home_team_id)} style={{ flex: 1, padding: '7px', borderRadius: 'var(--r-sm)', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: result.penalty_winner_id === match.home_team_id ? 'var(--gold)' : 'var(--surface)', color: result.penalty_winner_id === match.home_team_id ? '#fff' : 'var(--text-2)', border: '1px solid var(--gold-border)' }}>
                                {match.home.flag_emoji} {match.home.name}
                              </button>
                              <button onClick={() => setPenaltyWinner(match.id, match.away_team_id)} style={{ flex: 1, padding: '7px', borderRadius: 'var(--r-sm)', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: result.penalty_winner_id === match.away_team_id ? 'var(--gold)' : 'var(--surface)', color: result.penalty_winner_id === match.away_team_id ? '#fff' : 'var(--text-2)', border: '1px solid var(--gold-border)' }}>
                                {match.away.flag_emoji} {match.away.name}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── BONUSES TAB ────────────────────────────────────────────────────────────

function BonusesTab() {
  const [bonuses, setBonuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  React.useEffect(() => { loadBonuses() }, [])

  async function loadBonuses() {
    const { data } = await supabase
      .from('bonus_predictions')
      .select('*, entries(entry_name, users(name))')
      .order('category')
    setBonuses(data || [])
    setLoading(false)
  }

  async function toggleCorrect(id, current) {
    setSaving(true)
    const points = !current ? 8 : 0
    const { data, error } = await supabase
      .from('bonus_predictions')
      .update({ is_correct: !current, points_awarded: points })
      .eq('id', id)
      .select()
    if (error) { alert(`Failed to update bonus prediction: ${error.message}`); setSaving(false); return }
    if (!data || data.length === 0) {
      alert('Update was sent but no row was changed. This usually means a Supabase permissions (RLS) policy is blocking admin writes to bonus_predictions.')
      setSaving(false)
      return
    }
    await loadBonuses()
    setSaving(false)
  }

  if (loading) return <p className="text-muted">Loading bonus predictions...</p>

  const grouped = {}
  bonuses.forEach(b => { if (!grouped[b.category]) grouped[b.category] = []; grouped[b.category].push(b) })

  const categoryLabels = { golden_boot: 'Golden Boot', golden_gloves: 'Golden Gloves', player_tournament: 'Player of the Tournament' }

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', marginBottom: '8px' }}>Bonus Predictions</h2>
      <p style={{ color: 'var(--text-2)', fontSize: '13px', marginBottom: '20px' }}>
        Tick the answers that are correct. Players receive 8 points per correct bonus prediction.
      </p>

      {Object.entries(grouped).map(([category, preds]) => (
        <div key={category} style={{ marginBottom: '20px' }}>
          <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '12px' }}>{categoryLabels[category] || category}</h3>
          <div className="card-flat">
            <table>
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Entry</th>
                  <th>Answer</th>
                  <th style={{ textAlign: 'center' }}>Correct</th>
                </tr>
              </thead>
              <tbody>
                {preds.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontSize: '13px' }}>{b.entries?.users?.name}</td>
                    <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{b.entries?.entry_name}</td>
                    <td style={{ fontSize: '13px', fontWeight: b.is_correct ? '600' : '400', color: b.is_correct ? 'var(--green)' : 'var(--text)' }}>
                      {b.predicted_player_name}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button onClick={() => toggleCorrect(b.id, b.is_correct)} disabled={saving} style={{ width: '32px', height: '32px', borderRadius: 'var(--r-sm)', border: b.is_correct ? 'none' : '1px solid var(--border)', background: b.is_correct ? 'var(--green)' : 'var(--surface)', cursor: 'pointer', fontSize: '14px', fontWeight: '700', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                        {b.is_correct ? '✓' : ''}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {bonuses.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No bonus predictions submitted yet.</p>
        </div>
      )}
    </div>
  )
}

// ─── LEADERBOARD TAB ────────────────────────────────────────────────────────

function LeaderboardTab() {
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)

  React.useEffect(() => { loadLeaderboard() }, [])

  async function loadLeaderboard() {
    const { data } = await supabase.from('leaderboard').select('*')
    setPlayers(data || [])
    setLoading(false)
  }

  if (loading) return <p className="text-muted">Loading leaderboard...</p>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Leaderboard ({players.length} entries)</h2>
        <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '7px 14px' }} onClick={loadLeaderboard}>Refresh</button>
      </div>

      {players.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No paid entries yet.</p>
        </div>
      ) : (
        <div className="card-flat">
          <table>
            <thead>
              <tr>
                <th style={{ width: '48px' }}>#</th>
                <th>Entry</th>
                <th>Player</th>
                <th style={{ textAlign: 'right' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p, i) => (
                <tr key={p.id}>
                  <td>
                    <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '20px', color: i === 0 ? '#b45309' : i === 1 ? '#6b7280' : i === 2 ? '#92400e' : 'var(--text-3)' }}>
                      {i + 1}
                    </span>
                  </td>
                  <td style={{ fontWeight: i < 3 ? '600' : '400' }}>{p.entry_name}</td>
                  <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>{p.player_name}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '20px', color: i === 0 ? 'var(--gold)' : 'var(--text)' }}>
                      {p.total_points}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── BRACKET TAB ────────────────────────────────────────────────────────────

function BracketTab() {
  const [matches, setMatches] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [selections, setSelections] = useState({})

  React.useEffect(() => {
    async function load() {
      const { data: matchData } = await supabase
        .from('matches')
        .select('*')
        .eq('phase', 'knockout_r32')
        .order('match_number')

      const { data: teamData } = await supabase.from('teams').select('*').order('name')

      setMatches(matchData || [])
      setTeams(teamData || [])

      const initSelections = {}
      matchData?.forEach(m => { initSelections[m.id] = { home: m.home_team_id || '', away: m.away_team_id || '' } })
      setSelections(initSelections)
      setLoading(false)
    }
    load()
  }, [])

  async function handleSave() {
    setSaving(true)
    setSaved(false)
    for (const [matchId, sel] of Object.entries(selections)) {
      await supabase.from('matches').update({ home_team_id: sel.home || null, away_team_id: sel.away || null }).eq('id', matchId)
    }
    setSaved(true)
    setSaving(false)
  }

  if (loading) return <p className="text-muted">Loading bracket...</p>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0, marginBottom: '4px' }}>Knockout Bracket Setup</h2>
          <p style={{ color: 'var(--text-2)', fontSize: '13px' }}>Assign teams to the Round of 32 once the group stage is complete. The rest of the bracket auto-populates from player predictions.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, marginLeft: '16px' }}>
          {saved && <span style={{ fontSize: '13px', color: 'var(--gold)', fontWeight: '500' }}>Saved</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', fontSize: '13px' }}>
            {saving ? 'Saving...' : 'Save Bracket'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {matches.map(match => (
          <div key={match.id} className="card" style={{ marginBottom: 0, padding: '16px 20px' }}>
            <p style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '10px', fontWeight: '600', letterSpacing: '.06em', textTransform: 'uppercase' }}>
              M{match.match_number} - {match.match_label}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '10px', alignItems: 'center' }}>
              <select value={selections[match.id]?.home || ''} onChange={e => setSelections(prev => ({ ...prev, [match.id]: { ...prev[match.id], home: e.target.value } }))}>
                <option value="">Select home team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.flag_emoji} {t.name}</option>)}
              </select>
              <span style={{ color: 'var(--text-3)', fontWeight: '700', textAlign: 'center' }}>vs</span>
              <select value={selections[match.id]?.away || ''} onChange={e => setSelections(prev => ({ ...prev, [match.id]: { ...prev[match.id], away: e.target.value } }))}>
                <option value="">Select away team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.flag_emoji} {t.name}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── PHASE TAB ──────────────────────────────────────────────────────────────

function PhaseTab() {
  const [settings, setSettings] = useState({ phase1_open: true, phase2_open: false })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  React.useEffect(() => {
    async function load() {
      const { data: settingsData } = await supabase.from('settings').select('key, value').in('key', ['phase2_open'])
      const phase2Open = settingsData?.find(s => s.key === 'phase2_open')?.value === 'true'
      setSettings(prev => ({ ...prev, phase2_open: phase2Open }))
      const { data: entriesData } = await supabase.from('entries').select('phase1_locked').limit(1).single()
      if (entriesData) setSettings(prev => ({ ...prev, phase1_open: !entriesData.phase1_locked }))
      setLoading(false)
    }
    load()
  }, [])

  async function handleSave() {
    setSaving(true)
    setSaved(false)
    await supabase.from('entries').update({ phase1_locked: !settings.phase1_open }).gte('id', '00000000-0000-0000-0000-000000000000').gte('id', '00000000-0000-0000-0000-000000000000')
    await supabase.from('settings').update({ value: settings.phase2_open ? 'true' : 'false' }).eq('key', 'phase2_open')
    setSaved(true)
    setSaving(false)
  }

  if (loading) return <p className="text-muted">Loading...</p>

  const toggleStyle = (active) => ({
    padding: '8px 20px', borderRadius: 'var(--r)', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600',
    background: active ? 'var(--gold)' : 'var(--surface)',
    color: active ? '#fff' : 'var(--text-2)',
    border: active ? 'none' : '1px solid var(--border)'
  })

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', marginBottom: '8px' }}>Phase Control</h2>
      <p style={{ color: 'var(--text-2)', fontSize: '13px', marginBottom: '20px' }}>
        Control which phases are open for predictions. Locking Phase 1 prevents any further group stage edits.
      </p>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ fontWeight: '600', marginBottom: '4px' }}>Phase 1 - Group Stage</p>
            <p style={{ fontSize: '12px', color: 'var(--text-2)' }}>Allow players to submit and edit group stage predictions</p>
          </div>
          <button onClick={() => setSettings(prev => ({ ...prev, phase1_open: !prev.phase1_open }))} style={toggleStyle(settings.phase1_open)}>
            {settings.phase1_open ? 'Open' : 'Locked'}
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ fontWeight: '600', marginBottom: '4px' }}>Phase 2 - Knockout Stage</p>
            <p style={{ fontSize: '12px', color: 'var(--text-2)' }}>Allow players to submit knockout stage predictions</p>
          </div>
          <button onClick={() => setSettings(prev => ({ ...prev, phase2_open: !prev.phase2_open }))} style={toggleStyle(settings.phase2_open)}>
            {settings.phase2_open ? 'Open' : 'Locked'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        {saved && <p style={{ fontSize: '13px', color: 'var(--gold)', fontWeight: '500' }}>Settings saved.</p>}
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', fontSize: '13px' }}>
          {saving ? 'Saving...' : 'Save Phase Settings'}
        </button>
      </div>
    </div>
  )
}

// ─── ENTRY DETAIL MODAL ─────────────────────────────────────────────────────

function EntryDetailModal({ entry, onClose }) {
  const [predictions, setPredictions] = useState([])
  const [bonuses, setBonuses] = useState([])
  const [loading, setLoading] = useState(true)

  React.useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('match_predictions')
        .select(`*, matches(match_number, group_code, phase, actual_home_goals, actual_away_goals, result_confirmed, home:home_team_id(name, flag_emoji), away:away_team_id(name, flag_emoji))`)
        .eq('entry_id', entry.id)
      setPredictions((data || []).sort((a, b) => (a.matches?.match_number || 0) - (b.matches?.match_number || 0)))

      const { data: bonusData } = await supabase
        .from('bonus_predictions')
        .select('*')
        .eq('entry_id', entry.id)
      setBonuses(bonusData || [])

      setLoading(false)
    }
    load()
  }, [entry.id])

  const totalPoints = predictions.reduce((sum, p) => sum + (p.points_awarded || 0), 0)

  const categoryLabels = { golden_boot: 'Golden Boot', golden_gloves: 'Golden Gloves', player_tournament: 'Player of the Tournament' }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, overflowY: 'auto', padding: '40px 16px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', background: 'var(--surface)', borderRadius: 'var(--r-xl)', padding: '28px', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '24px', margin: 0, marginBottom: '4px' }}>{entry.entry_name}</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>{entry.users?.name} · {entry.users?.email}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontFamily: 'var(--ff-d)', fontSize: '28px', fontWeight: '700', color: 'var(--gold)' }}>{totalPoints} pts</span>
            <button className="btn btn-secondary" onClick={onClose} style={{ padding: '7px 14px', fontSize: '13px' }}>Close</button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted">Loading predictions...</p>
        ) : (
          <>
            {predictions.length === 0 ? (
              <p className="text-muted">No predictions submitted yet.</p>
            ) : (
              <div className="card-flat">
                <table>
                  <thead>
                    <tr>
                      <th>Match</th>
                      <th style={{ textAlign: 'center' }}>Prediction</th>
                      <th style={{ textAlign: 'center' }}>Actual</th>
                      <th style={{ textAlign: 'center' }}>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions.map(p => (
                      <tr key={p.id}>
                        <td style={{ fontSize: '13px' }}>
                          <span style={{ fontSize: '10px', color: 'var(--text-3)', marginRight: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                            {p.matches?.phase === 'group' ? `Grp ${p.matches?.group_code}` : p.matches?.phase?.replace('knockout_', '').replace('r32', 'R32').replace('r16', 'R16').replace('qf', 'QF').replace('sf', 'SF').replace('3rd', '3rd Place').replace('final', 'Final').toUpperCase()}
                          </span>
                          {p.matches?.home?.flag_emoji} {p.matches?.home?.name} vs {p.matches?.away?.name} {p.matches?.away?.flag_emoji}
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                          {p.predicted_home_goals} - {p.predicted_away_goals}
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                          {p.matches?.result_confirmed ? `${p.matches.actual_home_goals} - ${p.matches.actual_away_goals}` : '-'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {p.matches?.result_confirmed ? (
                            <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: p.points_awarded > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                              {p.points_awarded > 0 ? `+${p.points_awarded}` : '0'}
                            </span>
                          ) : <span style={{ color: 'var(--text-3)' }}>-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {bonuses.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '12px' }}>Bonus Predictions</h3>
                <div className="card-flat">
                  <table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Pick</th>
                        <th style={{ textAlign: 'center' }}>Correct</th>
                        <th style={{ textAlign: 'center' }}>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bonuses.map(b => (
                        <tr key={b.id}>
                          <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{categoryLabels[b.category] || b.category}</td>
                          <td style={{ fontSize: '13px', fontWeight: '500' }}>{b.predicted_player_name}</td>
                          <td style={{ textAlign: 'center', fontSize: '13px' }}>
                            {b.is_correct
                              ? <span style={{ color: 'var(--green)', fontWeight: '600' }}>✓</span>
                              : <span style={{ color: 'var(--text-3)' }}>-</span>}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: b.points_awarded > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                              {b.points_awarded > 0 ? `+${b.points_awarded}` : '0'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
// LMS ADMIN TAB

function LMSAdminTab() {
  const [activeLmsTab, setActiveLmsTab] = useState('rounds')
  const [jumpToGameweekId, setJumpToGameweekId] = useState(null)
  const LMS_TABS = ['rounds', 'gameweeks', 'fixtures', 'picks', 'standings', 'players']

  function viewGameweekPicks(gwId) {
    setJumpToGameweekId(gwId)
    setActiveLmsTab('picks')
  }

  return (
    <div>
      <div className="tab-bar">
        {LMS_TABS.map(tab => (
          <button key={tab} onClick={() => setActiveLmsTab(tab)} className={`tab-btn ${activeLmsTab === tab ? 'active' : ''}`}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeLmsTab === 'rounds' && <LMSRoundsTab />}
      {activeLmsTab === 'gameweeks' && <LMSGameweeksTab onViewPicks={viewGameweekPicks} />}
      {activeLmsTab === 'fixtures' && <LMSFixturesTab />}
      {activeLmsTab === 'picks' && <LMSPicksTab initialGameweekId={jumpToGameweekId} />}
      {activeLmsTab === 'standings' && <LMSStandingsTab />}
      {activeLmsTab === 'players' && <LMSPlayersTab />}
    </div>
  )
}

// D8 audit trail: who entered, who won, who picked what in which week, for
// one round. Reuses fetchGameweekPickRows (the same query the Picks tab
// uses) per gameweek rather than a separate query shape.
function LMSRoundAuditModal({ round, onClose }) {
  const [loading, setLoading] = useState(true)
  const [entries, setEntries] = useState([])
  const [pickLog, setPickLog] = useState([])

  React.useEffect(() => {
    async function load() {
      const [{ data: entriesData, error: entriesError }, { data: gwData, error: gwError }] = await Promise.all([
        supabase.from('lms_entries').select('*, users(name, email)').eq('round_id', round.id).order('joined_at'),
        supabase.from('lms_gameweeks').select('*').eq('round_id', round.id).order('gameweek_number')
      ])
      if (entriesError) alert(`Failed to load entries: ${entriesError.message}`)
      if (gwError) alert(`Failed to load gameweeks: ${gwError.message}`)

      setEntries(entriesData || [])

      const rowsPerGw = await Promise.all(
        (gwData || []).map(gw =>
          fetchGameweekPickRows(gw.id, round.id).then(rows => rows.map(r => ({ ...r, gwNumber: gw.gameweek_number })))
        )
      )
      setPickLog(rowsPerGw.flat().sort((a, b) => a.gwNumber - b.gwNumber || a.name.localeCompare(b.name)))
      setLoading(false)
    }
    load()
  }, [round.id])

  const winner = entries.find(e => e.id === round.winner_entry_id)
  const label = `${round.events?.short_code}-R${round.round_number}`

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, overflowY: 'auto', padding: '40px 16px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', background: 'var(--surface-1)', borderRadius: 'var(--r-xl)', padding: '28px', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '24px', margin: 0, marginBottom: '4px' }}>{label} audit</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>
              {round.events?.name} · R{round.pot_total} pot · {round.status.replace(/_/g, ' ')}
              {winner && <> · Winner: <strong style={{ color: 'var(--blue-bright)' }}>{winner.users?.name || winner.users?.email}</strong></>}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={onClose} style={{ padding: '7px 14px', fontSize: '13px' }}>Close</button>
        </div>

        {loading ? (
          <p className="text-muted">Loading audit trail...</p>
        ) : (
          <>
            <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '12px' }}>Entries</h3>
            {entries.length === 0 ? (
              <p className="text-muted" style={{ marginBottom: '24px' }}>No entries in this round.</p>
            ) : (
              <div className="card-flat" style={{ marginBottom: '24px' }}>
                <table>
                  <thead>
                    <tr><th>Player</th><th>Email</th><th>Joined</th><th style={{ textAlign: 'center' }}>Paid</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={e.id}>
                        <td style={{ fontWeight: '500', fontSize: '13px' }}>
                          {e.users?.name}{e.id === round.winner_entry_id ? ' 🏆' : ''}
                        </td>
                        <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{e.users?.email}</td>
                        <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{e.joined_at ? new Date(e.joined_at).toLocaleDateString() : '—'}</td>
                        <td style={{ textAlign: 'center', fontSize: '13px' }}>{e.paid_at ? '✓' : ''}</td>
                        <td style={{ fontSize: '13px', color: e.status === 'active' ? 'var(--green)' : e.status === 'eliminated' ? 'var(--red)' : 'var(--text-2)' }}>
                          {e.status.replace(/_/g, ' ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '4px' }}>Pick log</h3>
            <p className="text-muted" style={{ fontSize: '12px', marginBottom: '12px' }}>Every entry's pick in every gameweek of this round — for resolving player queries.</p>
            {pickLog.length === 0 ? (
              <p className="text-muted">No gameweeks in this round yet.</p>
            ) : (
              <div className="card-flat">
                <table>
                  <thead>
                    <tr><th>GW</th><th>Player</th><th>Team</th><th style={{ textAlign: 'center' }}>Auto</th><th>Outcome</th></tr>
                  </thead>
                  <tbody>
                    {pickLog.map((row, i) => {
                      const outcome = outcomeDisplay(row.outcome)
                      return (
                        <tr key={`${row.gwNumber}-${row.entryId}-${i}`}>
                          <td style={{ fontWeight: '600', fontSize: '13px' }}>{row.gwNumber}</td>
                          <td style={{ fontSize: '13px' }}>{row.name}</td>
                          <td style={{ fontSize: '13px' }}>{row.team || '—'}</td>
                          <td style={{ textAlign: 'center' }}>{row.isAutoPick ? <span className="badge-gray">Auto</span> : ''}</td>
                          <td style={{ fontSize: '13px', fontWeight: '600', color: outcome.color }}>{outcome.label}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function LMSRoundsTab() {
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newRound, setNewRound] = useState({ round_number: 1, entry_fee: 100, status: 'open_for_entry' })
  const [events, setEvents] = useState([])
  const [selectedEvent, setSelectedEvent] = useState('')
  const [auditRound, setAuditRound] = useState(null)

  React.useEffect(() => { loadData() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    const { data: eventsData, error: eventsError } = await supabase
      .from('events')
      .select('*')
      .eq('format_type', 'lms')
    if (eventsError) alert(`Failed to load events: ${eventsError.message}`)

    setEvents(eventsData || [])
    if (eventsData?.length > 0) setSelectedEvent(eventsData[0].id)

    const { data: roundsData, error: roundsError } = await supabase
      .from('lms_rounds')
      .select('*, events(name, short_code)')
      .order('round_number')
    if (roundsError) alert(`Failed to load rounds: ${roundsError.message}`)

    setRounds(roundsData || [])
    setLoading(false)
  }

  async function handleCreateRound() {
    if (!selectedEvent || !newRound.round_number) { alert('Please fill in all fields.'); return }
    setCreating(true)
    const { error } = await supabase.from('lms_rounds').insert({
      event_id: selectedEvent,
      round_number: parseInt(newRound.round_number),
      entry_fee: parseInt(newRound.entry_fee),
      status: newRound.status,
      pot_total: 0
    })
    if (error) { alert(`Failed to create round: ${error.message}`); setCreating(false); return }
    await loadData()
    setNewRound({ round_number: '', entry_fee: 100, status: 'open_for_entry' })
    setCreating(false)
  }

  async function handleStatusChange(roundId, status) {
    const { error } = await supabase.from('lms_rounds').update({ status }).eq('id', roundId)
    if (error) { alert(`Failed to update round status: ${error.message}`); return }
    await loadData()
  }

  async function handleDeleteRound(round) {
    const label = `${round.events?.short_code}-R${round.round_number}`

    const { data: gwRows, error: gwErr } = await supabase.from('lms_gameweeks').select('id').eq('round_id', round.id)
    if (gwErr) { alert(`Failed to check round contents: ${gwErr.message}`); return }
    const gwIds = (gwRows || []).map(g => g.id)

    let fixtureCount = 0
    if (gwIds.length > 0) {
      const { count, error: fixErr } = await supabase.from('lms_fixtures').select('id', { count: 'exact', head: true }).in('gameweek_id', gwIds)
      if (fixErr) { alert(`Failed to check round contents: ${fixErr.message}`); return }
      fixtureCount = count || 0
    }

    const { count: entryCount, error: entryErr } = await supabase.from('lms_entries').select('id', { count: 'exact', head: true }).eq('round_id', round.id)
    if (entryErr) { alert(`Failed to check round contents: ${entryErr.message}`); return }

    const { count: paidCount, error: paidErr } = await supabase.from('lms_entries').select('id', { count: 'exact', head: true }).eq('round_id', round.id).not('paid_at', 'is', null)
    if (paidErr) { alert(`Failed to check round contents: ${paidErr.message}`); return }

    const gwCount = gwIds.length

    if (paidCount > 0) {
      const typed = window.prompt(
        `${label} has ${paidCount} paid ${paidCount === 1 ? 'entry' : 'entries'}. The normal delete refuses this.\n\n`
        + `Only proceed if you have already handled these paid entries manually (refund, transfer, etc). Force-deleting will permanently remove ${label}, ${gwCount} gameweek${gwCount === 1 ? '' : 's'}, ${fixtureCount} fixture${fixtureCount === 1 ? '' : 's'}, and all ${entryCount} ${entryCount === 1 ? 'entry' : 'entries'} (including the paid ${paidCount === 1 ? 'one' : 'ones'}). This cannot be undone.\n\n`
        + `Type ${label} to confirm and force-delete:`
      )
      if (typed === null) return
      if (typed !== label) { alert('Confirmation text did not match. Round was not deleted.'); return }

      const { error } = await supabase.rpc('force_delete_lms_round', { p_round_id: round.id })
      if (error) { alert(`Failed to force-delete round: ${error.message}`); return }
      await loadData()
      return
    }

    const confirmMsg = `This will delete ${label}, ${gwCount} gameweek${gwCount === 1 ? '' : 's'}, ${fixtureCount} fixture${fixtureCount === 1 ? '' : 's'}, and ${entryCount} ${entryCount === 1 ? 'entry' : 'entries'}. This cannot be undone.`
    if (!window.confirm(confirmMsg)) return

    const { error } = await supabase.rpc('delete_lms_round', { p_round_id: round.id })
    if (error) { alert(`Failed to delete round: ${error.message}`); return }
    await loadData()
  }

  if (loading) return <p className="text-muted">Loading rounds...</p>

  const statusOptions = ['open_for_entry', 'active', 'complete', 'rolled_over']
  const formatStatus = s => s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Rounds</h2>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '16px' }}>Create New Round</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Event</label>
            <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Round Number</label>
            <input type="number" min="1" value={newRound.round_number} onChange={e => setNewRound(prev => ({ ...prev, round_number: e.target.value }))} placeholder="1" />
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Entry Fee (R)</label>
            <input type="number" min="0" value={newRound.entry_fee} onChange={e => setNewRound(prev => ({ ...prev, entry_fee: e.target.value }))} />
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Status</label>
            <select value={newRound.status} onChange={e => setNewRound(prev => ({ ...prev, status: e.target.value }))}>
              {statusOptions.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
            </select>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleCreateRound} disabled={creating} style={{ fontSize: '13px', padding: '8px 18px' }}>
          {creating ? 'Creating...' : 'Create Round'}
        </button>
      </div>

      {rounds.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No rounds created yet.</p>
        </div>
      ) : (
        <div className="card-flat">
          <table>
            <thead>
              <tr>
                <th>Event</th>
                <th>Round</th>
                <th>Entry Fee</th>
                <th>Pot Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rounds.map(round => (
                <tr key={round.id}>
                  <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{round.events?.name}</td>
                  <td style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '700' }}>{round.events?.short_code}-R{round.round_number}</td>
                  <td style={{ fontSize: '13px' }}>R{round.entry_fee}</td>
                  <td style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '700', color: 'var(--gold)' }}>R{round.pot_total}</td>
                  <td>
                    <select value={round.status} onChange={e => handleStatusChange(round.id, e.target.value)} style={{ fontSize: '12px', padding: '4px 8px', width: 'auto' }}>
                      {statusOptions.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
                    </select>
                  </td>
                  <td style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setAuditRound(round)}
                      style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--blue-bright)', color: 'var(--blue-bright)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDeleteRound(round)}
                      style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {auditRound && <LMSRoundAuditModal round={auditRound} onClose={() => setAuditRound(null)} />}
    </div>
  )
}

function LMSGameweeksTab({ onViewPicks }) {
  const [rounds, setRounds] = useState([])
  const [gameweeks, setGameweeks] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newGW, setNewGW] = useState({ round_id: '', gameweek_number: 1, status: 'upcoming' })
  const creatingRef = React.useRef(false)
  const [locking, setLocking] = useState(null)
  const [processing, setProcessing] = useState(null)
  const [unlocking, setUnlocking] = useState(null)

  React.useEffect(() => { loadData() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    const { data: roundsData, error: roundsError } = await supabase
      .from('lms_rounds')
      .select('*, events(name, short_code)')
      .order('round_number')
    if (roundsError) alert(`Failed to load rounds: ${roundsError.message}`)

    setRounds(roundsData || [])
    if (roundsData?.length > 0 && !newGW.round_id) {
      setNewGW(prev => ({ ...prev, round_id: roundsData[0].id }))
    }

    const { data: gwData, error: gwError } = await supabase
      .from('lms_gameweeks')
      .select('*, lms_rounds(round_number, events(short_code)), lms_fixtures(count)')
      .order('gameweek_number')
    if (gwError) alert(`Failed to load gameweeks: ${gwError.message}`)

    setGameweeks(gwData || [])
    setLoading(false)
  }

  async function handleCreateGameweek() {
    if (creatingRef.current) return
    if (!newGW.round_id || !newGW.gameweek_number) { alert('Please fill in all fields.'); return }
    creatingRef.current = true
    setCreating(true)
    const { error } = await supabase.from('lms_gameweeks').insert({
      round_id: newGW.round_id,
      gameweek_number: parseInt(newGW.gameweek_number),
      status: newGW.status
    })
    if (error) { alert(`Failed to create gameweek: ${error.message}`); creatingRef.current = false; setCreating(false); return }
    await loadData()
    setNewGW(prev => ({ ...prev, gameweek_number: prev.gameweek_number + 1 }))
    creatingRef.current = false
    setCreating(false)
  }

  async function handleLockGameweek(gwId, label) {
    if (!window.confirm(`Lock ${label}? This auto-picks for any active entry with no pick yet, then stops further pick changes.`)) return
    setLocking(gwId)
    const { error } = await supabase.rpc('lock_lms_gameweek', { p_gameweek_id: gwId })
    setLocking(null)
    if (error) { alert(`Failed to lock gameweek: ${error.message}`); return }
    await loadData()
  }

  async function handleUnlockGameweek(gwId, label) {
    if (!window.confirm(`Unlock ${label}? This reopens picks, removes the auto-picks it made, and reinstates any entries it auto-eliminated for having no eligible team. Player-made picks are left untouched.`)) return
    setUnlocking(gwId)
    const { error } = await supabase.rpc('unlock_lms_gameweek', { p_gameweek_id: gwId })
    setUnlocking(null)
    if (error) { alert(`Failed to unlock gameweek: ${error.message}`); return }
    await loadData()
  }

  async function handleProcessGameweek(gwId, label) {
    if (!window.confirm(`Process ${label}? This evaluates every pick against entered results, eliminates losers, burns used teams, and may complete or roll over the round. This cannot be undone.`)) return
    setProcessing(gwId)
    const { error } = await supabase.rpc('process_lms_gameweek', { p_gameweek_id: gwId })
    setProcessing(null)
    if (error) { alert(`Failed to process gameweek: ${error.message}`); return }
    await loadData()
  }

  async function handleDeleteGameweek(gwId, label) {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return
    const { error } = await supabase.rpc('delete_lms_gameweek', { p_gameweek_id: gwId })
    if (error) {
      if (error.message?.includes('has picks referencing it')) {
        alert(`Can't delete ${label} — players have already picked in this gameweek. Delete the fixtures/picks first, or leave the gameweek in place.`)
      } else {
        alert(`Failed to delete gameweek: ${error.message}`)
      }
      return
    }
    await loadData()
  }

  if (loading) return <p className="text-muted">Loading gameweeks...</p>

  const statusOptions = ['upcoming', 'locked', 'processed']

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Gameweeks</h2>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '16px' }}>Add Gameweek</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Round</label>
            <select value={newGW.round_id} onChange={e => setNewGW(prev => ({ ...prev, round_id: e.target.value }))}>
              {rounds.map(r => (
                <option key={r.id} value={r.id}>{r.events?.short_code}-R{r.round_number}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Gameweek Number</label>
            <input type="number" min="1" value={newGW.gameweek_number} onChange={e => setNewGW(prev => ({ ...prev, gameweek_number: e.target.value }))} />
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Status</label>
            <select value={newGW.status} onChange={e => setNewGW(prev => ({ ...prev, status: e.target.value }))}>
              {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '16px' }}>
          Deadline is set automatically from the earliest fixture kickoff once fixtures are added on the Fixtures tab.
        </p>
        <button className="btn btn-primary" onClick={handleCreateGameweek} disabled={creating} style={{ fontSize: '13px', padding: '8px 18px' }}>
          {creating ? 'Creating...' : 'Add Gameweek'}
        </button>
      </div>

      {gameweeks.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No gameweeks created yet.</p>
        </div>
      ) : (
        <div className="card-flat">
          <table>
            <thead>
              <tr>
                <th>Round</th>
                <th>Gameweek</th>
                <th style={{ textAlign: 'center' }}>Fixtures</th>
                <th>Deadline</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {gameweeks.map(gw => {
                const label = `${gw.lms_rounds?.events?.short_code}-R${gw.lms_rounds?.round_number} GW${gw.gameweek_number}`
                const fixtureCount = gw.lms_fixtures?.[0]?.count ?? 0
                return (
                  <tr key={gw.id}>
                    <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                      {gw.lms_rounds?.events?.short_code}-R{gw.lms_rounds?.round_number}
                    </td>
                    <td style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '700' }}>
                      GW{gw.gameweek_number}
                    </td>
                    <td style={{ textAlign: 'center', fontSize: '13px', fontWeight: fixtureCount === 0 ? '400' : '600', color: fixtureCount === 0 ? 'var(--text-3)' : 'var(--text)' }}>
                      {fixtureCount}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                      {gw.deadline ? new Date(gw.deadline).toLocaleString() : 'Not set — no fixtures yet'}
                    </td>
                    <td>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: 'var(--r-sm)',
                        color: gw.status === 'processed' ? 'var(--green)' : gw.status === 'locked' ? 'var(--gold)' : 'var(--text-3)',
                        border: `1px solid ${gw.status === 'processed' ? 'var(--green)' : gw.status === 'locked' ? 'var(--gold)' : 'var(--text-3)'}`
                      }}>
                        {gw.status.charAt(0).toUpperCase() + gw.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {gw.status === 'upcoming' && (
                        <button
                          onClick={() => handleLockGameweek(gw.id, label)}
                          disabled={locking === gw.id}
                          style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--gold)', color: 'var(--gold)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                        >
                          {locking === gw.id ? 'Locking...' : 'Lock'}
                        </button>
                      )}
                      {gw.status === 'locked' && (
                        <>
                          <button
                            onClick={() => handleProcessGameweek(gw.id, label)}
                            disabled={processing === gw.id}
                            style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--green)', color: 'var(--green)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                          >
                            {processing === gw.id ? 'Processing...' : 'Process'}
                          </button>
                          <button
                            onClick={() => handleUnlockGameweek(gw.id, label)}
                            disabled={unlocking === gw.id}
                            style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--text-3)', color: 'var(--text-3)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                          >
                            {unlocking === gw.id ? 'Unlocking...' : 'Unlock'}
                          </button>
                        </>
                      )}
                      {onViewPicks && (
                        <button
                          onClick={() => onViewPicks(gw.id)}
                          style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--blue-bright)', color: 'var(--blue-bright)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                        >
                          View picks
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteGameweek(gw.id, label)}
                        style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// The gameweek admin is most likely actively working on: the newest
// (highest-numbered) one that isn't processed yet, falling back to the
// newest overall. Defaulting to gwData[0] (oldest, ascending order) meant
// the Fixtures tab silently reopened on GW1 every time it was visited,
// which is how results/imports have landed in the wrong gameweek before --
// see D6/D7 in TEST_FEEDBACK_1.md.
function pickDefaultGameweek(gws) {
  if (!gws || gws.length === 0) return null
  const byNumberDesc = gws.slice().sort((a, b) => b.gameweek_number - a.gameweek_number)
  return byNumberDesc.find(g => g.status !== 'processed') || byNumberDesc[0]
}

function LMSFixturesTab() {
  const [gameweeks, setGameweeks] = useState([])
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [selectedGW, setSelectedGW] = useState('')
  const [newFixture, setNewFixture] = useState({ home_team: '', away_team: '', kickoff_at: '' })
  const [matchday, setMatchday] = useState('')
  const [importing, setImporting] = useState(false)
  const [locking, setLocking] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const [clearing, setClearing] = useState(false)
  const creatingRef = React.useRef(false)

  async function reloadFixtures() {
    const { data: fixData, error: fixError } = await supabase
      .from('lms_fixtures')
      .select('*, lms_gameweeks(gameweek_number, lms_rounds(round_number, events(short_code)))')
      .order('kickoff_at')
    if (fixError) alert(`Failed to reload fixtures: ${fixError.message}`)
    setFixtures(fixData || [])
  }

  async function reloadGameweeks() {
    const { data: gwData, error: gwError } = await supabase
      .from('lms_gameweeks')
      .select('*, lms_rounds(round_number, events(short_code))')
      .order('gameweek_number')
    if (gwError) alert(`Failed to reload gameweeks: ${gwError.message}`)
    setGameweeks(gwData || [])
    return gwData || []
  }

  React.useEffect(() => {
    async function load() {
      const gwData = await reloadGameweeks()
      const defaultGw = pickDefaultGameweek(gwData)
      if (defaultGw) {
        setSelectedGW(defaultGw.id)
        setMatchday(String(defaultGw.gameweek_number))
      }

      await reloadFixtures()
      setLoading(false)
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSelectGW(gwId) {
    setSelectedGW(gwId)
    const gw = gameweeks.find(g => g.id === gwId)
    if (gw) setMatchday(String(gw.gameweek_number))
  }

  async function handleAddFixture() {
    if (creatingRef.current) return
    if (!selectedGW || !newFixture.home_team || !newFixture.away_team || !newFixture.kickoff_at) { alert('Please fill in all fields, including kickoff time.'); return }
    creatingRef.current = true
    setCreating(true)
    const { error: insertError } = await supabase.from('lms_fixtures').insert({
      gameweek_id: selectedGW,
      home_team: newFixture.home_team,
      away_team: newFixture.away_team,
      kickoff_at: newFixture.kickoff_at,
      result: null,
      postponed: false
    })
    if (insertError) { alert(`Failed to add fixture: ${insertError.message}`); creatingRef.current = false; setCreating(false); return }
    await reloadFixtures()
    setNewFixture({ home_team: '', away_team: '', kickoff_at: '' })
    creatingRef.current = false
    setCreating(false)
  }

  async function handleImportFixtures() {
    if (!selectedGW || !matchday) { alert('Select a gameweek and a matchday number first.'); return }

    // D6/D7 root cause: importing a second matchday into a gameweek that
    // already has fixtures ADDS to them rather than replacing them, with no
    // warning -- that's how GW2 ended up with 19 fixtures spanning two
    // matchdays. Force an explicit choice instead of a silent pile-up.
    const existing = fixtures.filter(f => f.gameweek_id === selectedGW)
    if (existing.length > 0) {
      const dates = existing.map(f => new Date(f.kickoff_at)).sort((a, b) => a - b)
      const range = dates.length > 1
        ? `${dates[0].toLocaleDateString()} to ${dates[dates.length - 1].toLocaleDateString()}`
        : dates[0].toLocaleDateString()
      const proceed = window.confirm(
        `This gameweek already has ${existing.length} fixture${existing.length === 1 ? '' : 's'} loaded (${range}).\n\n`
        + `Importing matchday ${matchday} will ADD to these, not replace them -- if you're switching to a different matchday, cancel and use "Clear fixtures for this gameweek" first.\n\n`
        + `Continue and import matchday ${matchday} alongside what's already here?`
      )
      if (!proceed) return
    }

    setImporting(true)
    const { data, error } = await supabase.functions.invoke('import-lms-fixtures', {
      body: { gameweek_id: selectedGW, matchday: parseInt(matchday) }
    })
    setImporting(false)
    if (error) { alert(`Import failed: ${error.message}`); return }
    if (data?.error) { alert(`Import failed: ${data.error}`); return }

    let summary = `Imported: ${data.inserted} new, ${data.updated} updated.`
    if (data.unmappedTeamNames?.length > 0) {
      summary += `\n\nThe import still succeeded. These team names aren't in our short-name list yet (usually a newly promoted/relegated club) -- they've been saved using a best-guess short name, just double-check they read correctly: ${data.unmappedTeamNames.join(', ')}`
    }
    if (data.message) summary = data.message
    alert(summary)
    await reloadFixtures()
  }

  async function handleResultChange(fixtureId, result) {
    const { error: updateError } = await supabase.from('lms_fixtures').update({ result }).eq('id', fixtureId)
    if (updateError) { alert(`Failed to update result: ${updateError.message}`); return }
    await reloadFixtures()
  }

  async function handlePostponed(fixtureId, postponed) {
    const { error: updateError } = await supabase.from('lms_fixtures').update({ postponed }).eq('id', fixtureId)
    if (updateError) { alert(`Failed to update postponed flag: ${updateError.message}`); return }
    await reloadFixtures()
  }

  async function handleDeleteFixture(fixtureId, label) {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return
    const { error: deleteError } = await supabase.rpc('delete_lms_fixture', { p_fixture_id: fixtureId })
    if (deleteError) {
      if (deleteError.message?.includes('has picks referencing it')) {
        alert(`Can't delete ${label} — one or more players have already picked this fixture. Delete their picks first, or leave the fixture in place.`)
      } else {
        alert(`Failed to delete fixture: ${deleteError.message}`)
      }
      return
    }
    await reloadFixtures()
  }

  async function handleClearGameweekFixtures(gw) {
    const targets = fixtures.filter(f => f.gameweek_id === gw.id)
    if (targets.length === 0) return
    if (!window.confirm(`Delete all ${targets.length} fixture${targets.length === 1 ? '' : 's'} currently loaded for ${gwLabel(gw)}? This cannot be undone. Fixtures already picked by a player will be skipped.`)) return

    setClearing(true)
    let deleted = 0
    const blocked = []
    for (const f of targets) {
      const { error } = await supabase.rpc('delete_lms_fixture', { p_fixture_id: f.id })
      if (error) {
        blocked.push(`${f.home_team} vs ${f.away_team}`)
      } else {
        deleted++
      }
    }
    setClearing(false)
    await reloadFixtures()

    let summary = `Deleted ${deleted} of ${targets.length} fixture${targets.length === 1 ? '' : 's'}.`
    if (blocked.length > 0) {
      summary += `\n\nSkipped (already picked by a player): ${blocked.join(', ')}`
    }
    alert(summary)
  }

  async function handleLockGameweek(gw) {
    if (!window.confirm(`Lock ${gwLabel(gw)}? This auto-picks for any active entry with no pick yet, then stops further pick changes.`)) return
    setLocking(true)
    const { error } = await supabase.rpc('lock_lms_gameweek', { p_gameweek_id: gw.id })
    setLocking(false)
    if (error) { alert(`Failed to lock gameweek: ${error.message}`); return }
    await reloadGameweeks()
  }

  async function handleUnlockGameweek(gw) {
    if (!window.confirm(`Unlock ${gwLabel(gw)}? This reopens picks, removes the auto-picks it made, and reinstates any entries it auto-eliminated for having no eligible team. Player-made picks are left untouched.`)) return
    setUnlocking(true)
    const { error } = await supabase.rpc('unlock_lms_gameweek', { p_gameweek_id: gw.id })
    setUnlocking(false)
    if (error) { alert(`Failed to unlock gameweek: ${error.message}`); return }
    await reloadGameweeks()
  }

  async function handleProcessGameweek(gw) {
    if (!window.confirm(`Process ${gwLabel(gw)}? This evaluates every pick against the results entered below, eliminates losers, burns used teams, and may complete or roll over the round. This cannot be undone.`)) return
    setProcessing(true)
    const { error } = await supabase.rpc('process_lms_gameweek', { p_gameweek_id: gw.id })
    setProcessing(false)
    if (error) { alert(`Failed to process gameweek: ${error.message}`); return }
    await reloadGameweeks()
  }

  if (loading) return <p className="text-muted">Loading fixtures...</p>

  const resultOptions = [
    { value: '', label: 'Pending' },
    { value: 'home_win', label: 'Home Win' },
    { value: 'away_win', label: 'Away Win' },
    { value: 'draw', label: 'Draw' }
  ]

  const gwLabel = (gw) => `${gw.lms_rounds?.events?.short_code}-R${gw.lms_rounds?.round_number} GW${gw.gameweek_number}`
  const selectedGwRow = gameweeks.find(gw => gw.id === selectedGW)
  const visibleFixtures = fixtures.filter(f => f.gameweek_id === selectedGW)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Fixtures</h2>
      </div>

      <div className="card-gold card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '4px' }}>Import Fixtures from Premier League</h3>
        <p className="text-muted" style={{ fontSize: '12px', marginBottom: '16px' }}>
          Pulls that matchweek's fixtures and kickoff times from football-data.org. Safe to re-run: it updates kickoff times but never touches a result or postponed flag you've already entered.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Gameweek</label>
            <select value={selectedGW} onChange={e => handleSelectGW(e.target.value)}>
              {gameweeks.map(gw => (
                <option key={gw.id} value={gw.id}>{gwLabel(gw)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>PL Matchday</label>
            <input type="number" min="1" max="38" value={matchday} onChange={e => setMatchday(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleImportFixtures} disabled={importing} style={{ fontSize: '13px', padding: '8px 18px' }}>
          {importing ? 'Importing...' : 'Import Fixtures'}
        </button>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '16px' }}>Add Fixture Manually</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Gameweek</label>
            <select value={selectedGW} onChange={e => handleSelectGW(e.target.value)}>
              {gameweeks.map(gw => (
                <option key={gw.id} value={gw.id}>{gwLabel(gw)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Home Team</label>
            <input type="text" placeholder="e.g. Arsenal" value={newFixture.home_team} onChange={e => setNewFixture(prev => ({ ...prev, home_team: e.target.value }))} />
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Away Team</label>
            <input type="text" placeholder="e.g. Chelsea" value={newFixture.away_team} onChange={e => setNewFixture(prev => ({ ...prev, away_team: e.target.value }))} />
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Kickoff (required)</label>
            <input type="datetime-local" value={newFixture.kickoff_at} onChange={e => setNewFixture(prev => ({ ...prev, kickoff_at: e.target.value }))} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleAddFixture} disabled={creating} style={{ fontSize: '13px', padding: '8px 18px' }}>
          {creating ? 'Adding...' : 'Add Fixture'}
        </button>
      </div>

      {selectedGwRow && (
        <div className="card-flat" style={{ padding: '16px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '700' }}>{gwLabel(selectedGwRow)}</div>
            <span style={{
              fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: 'var(--r-sm)', display: 'inline-block', marginTop: '6px',
              color: selectedGwRow.status === 'processed' ? 'var(--green)' : selectedGwRow.status === 'locked' ? 'var(--blue-bright)' : 'var(--text-3)',
              border: `1px solid ${selectedGwRow.status === 'processed' ? 'var(--green)' : selectedGwRow.status === 'locked' ? 'var(--blue-bright)' : 'var(--text-3)'}`
            }}>
              {selectedGwRow.status.charAt(0).toUpperCase() + selectedGwRow.status.slice(1)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {visibleFixtures.length > 0 && selectedGwRow.status !== 'processed' && (
              <button
                onClick={() => handleClearGameweekFixtures(selectedGwRow)}
                disabled={clearing}
                style={{ fontSize: '12px', padding: '6px 12px', background: 'none', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
              >
                {clearing ? 'Clearing...' : 'Clear fixtures for this gameweek'}
              </button>
            )}
            {selectedGwRow.status === 'upcoming' && (
              <button
                onClick={() => handleLockGameweek(selectedGwRow)}
                disabled={locking}
                style={{ fontSize: '12px', padding: '6px 12px', background: 'none', border: '1px solid var(--blue-bright)', color: 'var(--blue-bright)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
              >
                {locking ? 'Locking...' : 'Lock gameweek'}
              </button>
            )}
            {selectedGwRow.status === 'locked' && (
              <>
                <button
                  onClick={() => handleProcessGameweek(selectedGwRow)}
                  disabled={processing}
                  style={{ fontSize: '12px', padding: '6px 12px', background: 'none', border: '1px solid var(--green)', color: 'var(--green)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                >
                  {processing ? 'Processing...' : 'Process gameweek — enter results below first'}
                </button>
                <button
                  onClick={() => handleUnlockGameweek(selectedGwRow)}
                  disabled={unlocking}
                  style={{ fontSize: '12px', padding: '6px 12px', background: 'none', border: '1px solid var(--text-3)', color: 'var(--text-3)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                >
                  {unlocking ? 'Unlocking...' : 'Unlock'}
                </button>
              </>
            )}
            {selectedGwRow.status === 'processed' && (
              <span className="text-muted" style={{ fontSize: '12px' }}>Processed — no further action needed here.</span>
            )}
          </div>
        </div>
      )}

      <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '12px' }}>
        Fixtures {selectedGwRow ? `— ${gwLabel(selectedGwRow)}` : ''}
      </h3>

      {visibleFixtures.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>
            {gameweeks.length === 0 ? 'No fixtures added yet.' : 'No fixtures for this gameweek yet.'}
          </p>
        </div>
      ) : (
        <div className="card-flat">
          <table>
            <thead>
              <tr>
                <th>Home</th>
                <th>Away</th>
                <th>Kickoff</th>
                <th>Result</th>
                <th style={{ textAlign: 'center' }}>Postponed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleFixtures.map(fix => {
                const label = `${gwLabel(fix.lms_gameweeks)}: ${fix.home_team} vs ${fix.away_team}`
                return (
                  <tr key={fix.id}>
                    <td style={{ fontWeight: '500', fontSize: '13px' }}>{fix.home_team}</td>
                    <td style={{ fontWeight: '500', fontSize: '13px' }}>{fix.away_team}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                      {fix.kickoff_at ? new Date(fix.kickoff_at).toLocaleString() : '-'}
                    </td>
                    <td>
                      <select
                        value={fix.result || ''}
                        onChange={e => handleResultChange(fix.id, e.target.value || null)}
                        style={{ fontSize: '12px', padding: '4px 8px', width: 'auto' }}
                      >
                        {resultOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={fix.postponed || false}
                        onChange={e => handlePostponed(fix.id, e.target.checked)}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                    </td>
                    <td>
                      <button
                        onClick={() => handleDeleteFixture(fix.id, label)}
                        style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Shared by the Picks tab and the round audit drill-down (D8) so there's one
// query shape for "every entry in this gameweek's round, picked or not" --
// see TEST_FEEDBACK_1.md D8 ("build on the existing Picks tab data rather
// than duplicating it").
async function fetchGameweekPickRows(gwId, roundId) {
  const [{ data: entriesData, error: entriesError }, { data: picksData, error: picksError }] = await Promise.all([
    supabase.from('lms_entries').select('id, status, eliminated_gameweek_id, users(name, email)').eq('round_id', roundId),
    supabase.from('lms_picks').select('*').eq('gameweek_id', gwId)
  ])

  if (entriesError) alert(`Failed to load entries: ${entriesError.message}`)
  if (picksError) alert(`Failed to load picks: ${picksError.message}`)

  const entries = entriesData || []

  // An entry's eliminated_gameweek_id can point at an EARLIER gameweek than
  // the one currently selected (they've been out for a while) -- resolve
  // every distinct one to its number so "Eliminated GW1" is always correct,
  // not just for eliminations that happened in the selected gameweek.
  const eliminatedGwIds = [...new Set(entries.map(e => e.eliminated_gameweek_id).filter(Boolean))]
  let gwNumberById = new Map()
  if (eliminatedGwIds.length > 0) {
    const { data: gwRows, error: gwErr } = await supabase
      .from('lms_gameweeks')
      .select('id, gameweek_number')
      .in('id', eliminatedGwIds)
    if (gwErr) alert(`Failed to load elimination gameweeks: ${gwErr.message}`)
    gwNumberById = new Map((gwRows || []).map(g => [g.id, g.gameweek_number]))
  }

  const pickByEntry = new Map((picksData || []).map(p => [p.entry_id, p]))
  return entries.map(entry => {
    const pick = pickByEntry.get(entry.id) || null
    const noPickButEliminatedHere = !pick && entry.eliminated_gameweek_id === gwId
    return {
      entryId: entry.id,
      name: entry.users?.name || entry.users?.email || 'Unknown player',
      team: pick?.team_picked || null,
      isAutoPick: pick?.is_auto_pick || false,
      outcome: pick?.outcome || (noPickButEliminatedHere ? 'no_eligible_team' : null),
      entryStatus: entry.status,
      eliminatedGwNumber: entry.eliminated_gameweek_id ? (gwNumberById.get(entry.eliminated_gameweek_id) ?? null) : null
    }
  })
}

function outcomeDisplay(outcome) {
  if (outcome === 'survived') return { label: 'Survived', color: 'var(--green)' }
  if (outcome === 'eliminated') return { label: 'Eliminated', color: 'var(--red)' }
  if (outcome === 'void') return { label: 'Void (postponed)', color: 'var(--text-3)' }
  if (outcome === 'no_eligible_team') return { label: 'Eliminated — no eligible team', color: 'var(--red)' }
  if (outcome === 'pending') return { label: 'Pending', color: 'var(--text-3)' }
  return { label: '—', color: 'var(--text-3)' }
}

// Alive-for-picking = active or pending_payment (a pending entry still plays
// normally, per the manual/soft payment model -- see migration 018). Every
// other status (eliminated/winner/removed) is settled and never needs a
// pick chased, regardless of whether it happens to have a team recorded for
// this gameweek.
function entryStatusMeta(row) {
  const isAlive = row.entryStatus === 'active' || row.entryStatus === 'pending_payment'
  if (isAlive) {
    const hasPick = !!row.team
    return {
      isAlive: true,
      needsChase: !hasPick,
      label: hasPick ? 'Alive' : 'No pick yet',
      cls: hasPick ? 'badge-green' : 'badge-amber'
    }
  }
  if (row.entryStatus === 'eliminated') {
    return {
      isAlive: false,
      needsChase: false,
      label: row.eliminatedGwNumber ? `Eliminated GW${row.eliminatedGwNumber}` : 'Eliminated',
      cls: 'badge-red'
    }
  }
  if (row.entryStatus === 'winner') return { isAlive: false, needsChase: false, label: 'Winner', cls: 'badge-blue' }
  if (row.entryStatus === 'removed') return { isAlive: false, needsChase: false, label: 'Removed', cls: 'badge-gray' }
  return { isAlive: false, needsChase: false, label: row.entryStatus, cls: 'badge-gray' }
}

function gwLabel(gw) {
  return `${gw.lms_rounds?.events?.short_code}-R${gw.lms_rounds?.round_number} GW${gw.gameweek_number}`
}

function LMSPicksTab({ initialGameweekId }) {
  const [gameweeks, setGameweeks] = useState([])
  const [selectedGW, setSelectedGW] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingRows, setLoadingRows] = useState(false)

  React.useEffect(() => {
    async function load() {
      const { data: gwData, error: gwError } = await supabase
        .from('lms_gameweeks')
        .select('*, lms_rounds(round_number, events(short_code))')
        .order('gameweek_number')
      if (gwError) alert(`Failed to load gameweeks: ${gwError.message}`)

      setGameweeks(gwData || [])
      const initial = initialGameweekId && gwData?.some(g => g.id === initialGameweekId)
        ? initialGameweekId
        : gwData?.[0]?.id
      if (initial) setSelectedGW(initial)
      setLoading(false)
    }
    load()
  }, [initialGameweekId])

  React.useEffect(() => {
    if (!selectedGW) { setRows([]); return }
    setLoadingRows(true)
    const gw = gameweeks.find(g => g.id === selectedGW)
    fetchGameweekPickRows(selectedGW, gw?.round_id).then(merged => {
      setRows(merged)
      setLoadingRows(false)
    })
  }, [selectedGW]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p className="text-muted">Loading picks...</p>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Picks</h2>
        {gameweeks.length > 0 && (
          <select value={selectedGW} onChange={e => setSelectedGW(e.target.value)} style={{ fontSize: '13px', padding: '7px 12px', width: 'auto' }}>
            {gameweeks.map(gw => <option key={gw.id} value={gw.id}>{gwLabel(gw)}</option>)}
          </select>
        )}
      </div>

      <p className="text-muted" style={{ fontSize: '12px', marginBottom: '16px' }}>
        Every entry in this gameweek's round. Alive players with no pick yet are sorted to the top — they're the only ones to chase. Eliminated/settled players are grouped and greyed at the bottom.
      </p>

      {gameweeks.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No gameweeks created yet.</p>
        </div>
      ) : loadingRows ? (
        <p className="text-muted">Loading entries...</p>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No entries in this round.</p>
        </div>
      ) : (
        <div className="card-flat">
          <table>
            <thead>
              <tr>
                <th>Player</th>
                <th>Status</th>
                <th>Team</th>
                <th style={{ textAlign: 'center' }}>Auto</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows
                .map(row => ({ row, meta: entryStatusMeta(row) }))
                .sort((a, b) => {
                  const rank = m => (m.needsChase ? 0 : m.isAlive ? 1 : 2)
                  const byRank = rank(a.meta) - rank(b.meta)
                  return byRank !== 0 ? byRank : a.row.name.localeCompare(b.row.name)
                })
                .map(({ row, meta }) => {
                  const outcome = outcomeDisplay(row.outcome)
                  return (
                    <tr key={row.entryId} style={meta.isAlive ? undefined : { opacity: 0.55 }}>
                      <td style={{ fontWeight: '500', fontSize: '13px' }}>{row.name}</td>
                      <td>
                        <span className={meta.cls} style={meta.needsChase ? { fontWeight: '700' } : undefined}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ fontSize: '13px' }}>{row.team || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{row.isAutoPick ? <span className="badge-gray">Auto</span> : ''}</td>
                      <td style={{ fontSize: '13px', fontWeight: '600', color: outcome.color }}>{outcome.label}</td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function LMSStandingsTab() {
  const [standings, setStandings] = useState([])
  const [edited, setEdited] = useState({})
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newTeam, setNewTeam] = useState({ team_name: '', position: '' })
  const [adding, setAdding] = useState(false)

  React.useEffect(() => { loadStandings() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadStandings() {
    const { data, error } = await supabase.from('pl_standings').select('*').order('position')
    if (error) alert(`Failed to load standings: ${error.message}`)
    setStandings(data || [])
    setEdited({})
    setLoading(false)
  }

  async function handleImportStandings() {
    setImporting(true)
    const { data, error } = await supabase.functions.invoke('import-lms-standings', { body: {} })
    setImporting(false)
    if (error) { alert(`Import failed: ${error.message}`); return }
    if (data?.error) { alert(`Import failed: ${data.error}`); return }

    let summary = `Updated ${data.updated} team${data.updated === 1 ? '' : 's'}.`
    if (data.unmappedTeamNames?.length > 0) {
      summary += `\n\nThe import still succeeded. These team names aren't in our short-name list yet (usually a newly promoted/relegated club) -- they've been saved using a best-guess short name, just double-check they read correctly: ${data.unmappedTeamNames.join(', ')}`
    }
    alert(summary)
    await loadStandings()
  }

  function handlePositionChange(id, value) {
    setEdited(prev => ({ ...prev, [id]: value }))
  }

  async function handleSaveChanges() {
    const ids = Object.keys(edited)
    if (ids.length === 0) { alert('No position changes to save.'); return }
    setSaving(true)
    const now = new Date().toISOString()
    for (const id of ids) {
      const position = parseInt(edited[id])
      if (!Number.isFinite(position)) continue
      const { error } = await supabase.from('pl_standings').update({ position, updated_at: now }).eq('id', id)
      if (error) { alert(`Failed to save position: ${error.message}`); setSaving(false); return }
    }
    setSaving(false)
    await loadStandings()
  }

  async function handleAddTeam() {
    if (!newTeam.team_name || !newTeam.position) { alert('Please fill in team name and position.'); return }
    setAdding(true)
    const { error } = await supabase.from('pl_standings').insert({
      team_name: newTeam.team_name,
      position: parseInt(newTeam.position)
    })
    if (error) { alert(`Failed to add team: ${error.message}`); setAdding(false); return }
    await loadStandings()
    setNewTeam({ team_name: '', position: '' })
    setAdding(false)
  }

  async function handleDeleteStanding(id, teamName) {
    if (!window.confirm(`Remove ${teamName} from pl_standings?`)) return
    const { error } = await supabase.from('pl_standings').delete().eq('id', id)
    if (error) { alert(`Failed to remove team: ${error.message}`); return }
    await loadStandings()
  }

  if (loading) return <p className="text-muted">Loading standings...</p>

  const hasEdits = Object.keys(edited).length > 0

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Standings</h2>
      </div>

      <div className="card-gold card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '4px' }}>Import Standings from Premier League</h3>
        <p className="text-muted" style={{ fontSize: '12px', marginBottom: '16px' }}>
          Pulls the current PL table from football-data.org. Safe to re-run: upserts by team name, refreshes every team's position each time.
          Pre-season, the API returns every team on 0 points in an arbitrary order (typically alphabetical) rather than by form -- auto-pick will
          still work off whatever order is loaded, it just won't reflect real form until matchday 1 results are in. Edit positions by hand below
          if you want a specific GW1 order instead.
        </p>
        <button className="btn btn-primary" onClick={handleImportStandings} disabled={importing} style={{ fontSize: '13px', padding: '8px 18px' }}>
          {importing ? 'Importing...' : 'Import Standings'}
        </button>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '16px' }}>Add Team</h3>
        <p className="text-muted" style={{ fontSize: '12px', marginBottom: '16px' }}>
          Fallback for seeding by hand, or a newly promoted club the import couldn't map.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Team Name</label>
            <input type="text" value={newTeam.team_name} onChange={e => setNewTeam(prev => ({ ...prev, team_name: e.target.value }))} placeholder="e.g. Wolves" />
          </div>
          <div>
            <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Position</label>
            <input type="number" min="1" value={newTeam.position} onChange={e => setNewTeam(prev => ({ ...prev, position: e.target.value }))} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleAddTeam} disabled={adding} style={{ fontSize: '13px', padding: '8px 18px' }}>
          {adding ? 'Adding...' : 'Add Team'}
        </button>
      </div>

      {standings.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No standings loaded yet. Import from the API or add teams by hand above.</p>
        </div>
      ) : (
        <div className="card-flat">
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>Team</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {standings.map(s => (
                <tr key={s.id}>
                  <td style={{ width: '90px' }}>
                    <input
                      type="number"
                      min="1"
                      value={edited[s.id] ?? s.position}
                      onChange={e => handlePositionChange(s.id, e.target.value)}
                      style={{ width: '70px', fontSize: '13px', padding: '4px 8px' }}
                    />
                  </td>
                  <td style={{ fontFamily: 'var(--ff-d)', fontSize: '16px', fontWeight: '700' }}>{s.team_name}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                    {s.updated_at ? new Date(s.updated_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteStanding(s.id, s.team_name)}
                      style={{ fontSize: '12px', padding: '4px 10px', background: 'none', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '16px' }}>
            <button className="btn btn-primary" onClick={handleSaveChanges} disabled={!hasEdits || saving} style={{ fontSize: '13px', padding: '8px 18px' }}>
              {saving ? 'Saving...' : 'Save Position Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function LMSPlayersTab() {
  const [rounds, setRounds] = useState([])
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRound, setSelectedRound] = useState('')
  const [search, setSearch] = useState('')
  const [paymentFilter, setPaymentFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  React.useEffect(() => {
    async function load() {
      const { data: roundsData, error: roundsError } = await supabase
        .from('lms_rounds')
        .select('*, events(name, short_code)')
        .order('round_number')
      if (roundsError) alert(`Failed to load rounds: ${roundsError.message}`)

      setRounds(roundsData || [])
      if (roundsData?.length > 0) {
        setSelectedRound(roundsData[0].id)
        await loadEntries(roundsData[0].id)
      }
      setLoading(false)
    }
    load()
  }, [])

  async function loadEntries(roundId) {
    const { data, error } = await supabase
      .from('lms_entries')
      .select('*, users(name, email)')
      .eq('round_id', roundId)
      .order('joined_at')
    if (error) alert(`Failed to load entries: ${error.message}`)

    setEntries(data || [])
  }

  async function handleRoundChange(roundId) {
    setSelectedRound(roundId)
    await loadEntries(roundId)
  }

  // Payment is fully manual and soft: Pending/Paid go through
  // set_lms_entry_paid() (keeps fee_paid/pot_total in sync, and a pending
  // entry still plays normally -- it is never auto-blocked or eliminated).
  // Remove is the only way to take someone out for non-payment, via
  // remove_lms_entry() (reverses any pot contribution, then marks the entry
  // 'removed' -- a status, not a delete, so the join/pick history stays
  // auditable).
  async function handlePaymentAction(entry, action) {
    if (action === 'remove') {
      if (!window.confirm(`Remove ${entry.users?.name || 'this player'} from the round? This reverses any pot contribution already counted for them and cannot be undone from here.`)) return
      const { error } = await supabase.rpc('remove_lms_entry', { p_entry_id: entry.id })
      if (error) { alert(`Failed to remove entry: ${error.message}`); return }
    } else {
      const { error } = await supabase.rpc('set_lms_entry_paid', { p_entry_id: entry.id, p_paid: action === 'paid' })
      if (error) { alert(`Failed to update payment status: ${error.message}`); return }
    }
    await loadEntries(selectedRound)
  }

  // pending_payment <-> active must go through the Paid dropdown (via
  // set_lms_entry_paid) so fee_paid and lms_rounds.pot_total stay in sync.
  // This control is for the eliminated/winner overrides only.
  async function handleStatusChange(entryId, status) {
    if (status === 'active' || status === 'pending_payment') {
      alert('Use the Paid dropdown to move an entry between Pending and Paid — it keeps the fee and pot total in sync.')
      await loadEntries(selectedRound)
      return
    }
    const { error } = await supabase.from('lms_entries').update({ status }).eq('id', entryId)
    if (error) { alert(`Failed to update status: ${error.message}`); return }
    await loadEntries(selectedRound)
  }

  if (loading) return <p className="text-muted">Loading players...</p>

  const currentRound = rounds.find(r => r.id === selectedRound)
  const alive = entries.filter(e => e.status === 'active').length
  const eliminated = entries.filter(e => e.status === 'eliminated').length
  const removed = entries.filter(e => e.status === 'removed').length
  // Excludes 'removed': their pot contribution was already reversed, so
  // counting a lingering paid_at here would overstate who's actually in.
  const paid = entries.filter(e => e.paid_at && e.status !== 'removed').length

  function reference(entry) {
    if (!currentRound) return '—'
    return `${currentRound.events?.short_code}-R${currentRound.round_number}-${entry.id.slice(0, 8).toUpperCase()}`
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  // Paid/pending filters (and the "Paid" stat above) exclude 'removed':
  // its pot contribution was already reversed by remove_lms_entry, so a
  // lingering historical paid_at no longer means "currently paid in".
  const filteredEntries = entries.filter(entry => {
    if (paymentFilter === 'paid' && !(entry.paid_at && entry.status !== 'removed')) return false
    if (paymentFilter === 'pending' && !(!entry.paid_at && entry.status !== 'removed')) return false

    if (statusFilter === 'alive' && !(entry.status === 'active' || entry.status === 'pending_payment')) return false
    if (statusFilter === 'eliminated' && entry.status !== 'eliminated') return false
    if (statusFilter === 'winner' && entry.status !== 'winner') return false
    if (statusFilter === 'removed' && entry.status !== 'removed') return false

    if (search.trim()) {
      const q = search.trim().toLowerCase()
      const haystack = `${entry.users?.name || ''} ${entry.users?.email || ''} ${reference(entry)}`.toLowerCase()
      if (!haystack.includes(q)) return false
    }

    return true
  })

  function sortValue(entry, key) {
    if (key === 'name') return (entry.users?.name || '').toLowerCase()
    if (key === 'reference') return reference(entry)
    if (key === 'email') return (entry.users?.email || '').toLowerCase()
    if (key === 'paid') return entry.paid_at ? 1 : 0
    if (key === 'status') return entry.status || ''
    return ''
  }

  const visibleEntries = sortKey
    ? [...filteredEntries].sort((a, b) => {
        const va = sortValue(a, sortKey)
        const vb = sortValue(b, sortKey)
        if (va < vb) return sortDir === 'asc' ? -1 : 1
        if (va > vb) return sortDir === 'asc' ? 1 : -1
        return 0
      })
    : filteredEntries

  function sortArrow(key) {
    if (sortKey !== key) return null
    return sortDir === 'asc' ? ' ▲' : ' ▼'
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Players</h2>
        <select value={selectedRound} onChange={e => handleRoundChange(e.target.value)} style={{ fontSize: '13px', padding: '7px 12px', width: 'auto' }}>
          {rounds.map(r => (
            <option key={r.id} value={r.id}>{r.events?.short_code}-R{r.round_number}</option>
          ))}
        </select>
      </div>

      {currentRound && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '24px' }}>
          {[
            [entries.length, 'Total entries', 'var(--text)'],
            [paid, 'Paid', 'var(--gold)'],
            [alive, 'Alive', 'var(--green)'],
            [eliminated, 'Eliminated', 'var(--red)'],
            [removed, 'Removed', 'var(--text-3)'],
            [`R${currentRound.pot_total}`, 'Pot total', 'var(--gold)']
          ].map(([v, l, c]) => (
            <div key={l} className="stat-card">
              <div className="stat-num" style={{ color: c }}>{v}</div>
              <div className="stat-label">{l}</div>
            </div>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-2)' }}>No entries for this round yet.</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search name, email, or reference..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: '1 1 240px', fontSize: '13px', padding: '8px 12px' }}
            />
            <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)} style={{ fontSize: '13px', padding: '8px 12px', width: 'auto' }}>
              <option value="all">All payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: '13px', padding: '8px 12px', width: 'auto' }}>
              <option value="all">All statuses</option>
              <option value="alive">Alive</option>
              <option value="eliminated">Eliminated</option>
              <option value="winner">Winner</option>
              <option value="removed">Removed</option>
            </select>
          </div>

          {visibleEntries.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-2)' }}>No players match your search or filters.</p>
            </div>
          ) : (
            <div className="card-flat">
              <table>
                <thead>
                  <tr>
                    {[
                      ['name', 'Player'],
                      ['reference', 'Reference'],
                      ['email', 'Email'],
                      ['paid', 'Paid'],
                      ['status', 'Status']
                    ].map(([key, label]) => (
                      <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        {label}{sortArrow(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleEntries.map(entry => (
                    <tr key={entry.id}>
                      <td style={{ fontWeight: '500', fontSize: '13px' }}>{entry.users?.name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-2)' }}>{reference(entry)}</td>
                      <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{entry.users?.email}</td>
                      <td>
                        {/* Always editable, regardless of round/game status, so payment can be
                            reconciled at any point -- see migration 019. Only a removed entry
                            (financially settled by remove_lms_entry) has nothing left to adjust. */}
                        {entry.status === 'removed' ? (
                          <span className="badge-gray">Removed</span>
                        ) : (
                          <select
                            value={entry.paid_at ? 'paid' : 'pending'}
                            onChange={e => handlePaymentAction(entry, e.target.value)}
                            style={{
                              fontSize: '12px', padding: '4px 8px', width: 'auto',
                              color: entry.paid_at ? 'var(--green)' : 'var(--text-2)'
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="remove">Remove from round</option>
                          </select>
                        )}
                      </td>
                      <td>
                        {entry.status === 'removed' ? (
                          <span className="badge-gray">Removed</span>
                        ) : (
                          <select
                            value={entry.status}
                            onChange={e => handleStatusChange(entry.id, e.target.value)}
                            style={{ fontSize: '12px', padding: '4px 8px', width: 'auto',
                              color: entry.status === 'active' ? 'var(--green)' : entry.status === 'eliminated' ? 'var(--red)' : 'var(--text-2)'
                            }}
                          >
                            <option value="pending_payment">Pending Payment</option>
                            <option value="active">Active</option>
                            <option value="eliminated">Eliminated</option>
                            <option value="winner">Winner</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}